import {
  AssetReportContextItem,
  formatAssetReportLabel,
} from '@digital-twins/deck-ui/components/charts/asset-report-label'
import { CHART_LIMITS } from '@digital-twins/deck-ui/foundations/slide-limits'

import {
  StudyFigureCut,
  StudyFigureFilter,
  StudyFiguresMeta,
  StudyFiguresMetaCut,
} from '@digital-twins/shared-types'

/** The dropdown group a dimension is listed under (today's four groups). */
export type FigureDimensionGroup = 'segment' | 'survey' | 'population' | 'asset'

/** The combined saved-segment cut: one group per listed tag, no complement. */
export const ALL_SAVED_SEGMENTS_KEY = 'segment:all'

/** One group of a dimension, as the figure service forms it on the whole sample. */
export interface FigureDimensionValue {
  /** The group's id in a figures response (`StudyFigureGroup.sourceId`). */
  id: string
  label: string
  /** The group's size on the whole sample (Python's count). */
  n: number
  /** The filter that keeps this group (a lane-merged card filters, then unfolds). */
  filter: StudyFigureFilter
}

/**
 * One way the survey tab can split a card: a cut the figure service listed
 * (plots plan D3), so it never answers 400. `values` are its groups.
 */
export interface FigureDimension {
  key: string
  label: string
  group: FigureDimensionGroup
  cut: StudyFigureCut
  values: Array<FigureDimensionValue>
  /** The value a lane-merged card opens on before the largest: a saved segment's members. */
  preferredValueId: string | null
}

interface CanonicalQuestionMapEntry {
  questionId?: string
  canonicalType?: string
}

export interface BuildFigureDimensionsInput {
  meta: StudyFiguresMeta
  /** The card's own question name (never offered as its own split). */
  questionName: string
  /** Survey questions the tab offers as splits (today's segmentable list, labels normalised). */
  surveyQuestions: Array<any>
  canonicalQuestionMap?: Record<string, CanonicalQuestionMapEntry | undefined> | null
  /** The compiled `assetContext` items: an answer that is a tested item reads "Ad 1: Name". */
  assetContextItems?: Array<AssetReportContextItem> | null
  labels: {
    /** Today's "Asset shown" dimension name. */
    assignedAsset: string
    /** The combined saved-segment cut ("All subsegments"), in the UI locale. */
    allSubsegments: string
  }
}

function questionIdOf(
  question: any,
  canonicalQuestionMap: BuildFigureDimensionsInput['canonicalQuestionMap'],
): string | null {
  return canonicalQuestionMap?.[question?.name]?.questionId ?? question?._canonical?.questionId ?? null
}

/**
 * The survey's own text for a value it declares (choices or rating values),
 * else null. An answer that is a tested item reads as the report names it
 * (its name, `formatAssetReportLabel`): the survey's text is what twins read,
 * the ordinal.
 */
function declaredValueText(
  question: any,
  valueId: string,
  assetContextItems: BuildFigureDimensionsInput['assetContextItems'],
): string | null {
  const declared: Array<any> = [
    ...(Array.isArray(question?.choices) ? question.choices : []),
    ...(Array.isArray(question?.rateValues) ? question.rateValues : []),
  ]
  for (const choice of declared) {
    if (choice != null && typeof choice === 'object') {
      if (String(choice.value) === valueId) {
        const text = choice.text ?? choice.title ?? choice.label
        if (text == null) return null
        const refIds: Array<string> = (choice._canonical?.assetRefs ?? [])
          .map((ref: any) => ref?.assetId)
          .filter((id: unknown): id is string => typeof id === 'string')
        return formatAssetReportLabel(String(text), assetContextItems, [valueId, ...refIds])
      }
    } else if (choice != null && String(choice) === valueId) {
      return String(choice)
    }
  }
  return null
}

function serviceLabel(value: StudyFiguresMetaCut['values'][number]): string {
  return value.label
}

/**
 * A cut's values with their labels. A complement group ("Not in X", "Did not
 * pick X") keeps the service's label, resolved in the UI locale.
 */
function dimensionValues(
  entry: StudyFiguresMetaCut,
  labelOf: (value: StudyFiguresMetaCut['values'][number]) => string = serviceLabel,
): Array<FigureDimensionValue> {
  return entry.values.map((value) => ({ id: value.id, label: labelOf(value), n: value.n, filter: value.filter }))
}

/**
 * Today's segment quality rule (`segmentQuality` in QuestionCard), read from
 * the service's group sizes instead of aggregating responses: at least two
 * groups with someone in them, at least four answers, no more groups than a
 * bar chart draws, and not a sparse spread of singletons.
 */
export function isUsableFigureDimension(dimension: FigureDimension): boolean {
  const present = dimension.values.filter((value) => value.n > 0)
  const uniqueCount = present.length
  const totalCount = present.reduce((sum, value) => sum + value.n, 0)
  const singletonCount = present.filter((value) => value.n === 1).length
  const singletonShare = uniqueCount > 0 ? singletonCount / uniqueCount : 0
  const isTooMany = uniqueCount > CHART_LIMITS.horizontalBarMaxItems
  const isTooSparse = uniqueCount >= 6 && singletonShare >= 0.7
  return uniqueCount >= 2 && totalCount >= 4 && !isTooMany && !isTooSparse
}

/**
 * Every split the survey tab offers one card, from the figure service's own
 * list (never guessed from the survey): saved segments (members against
 * everyone else), survey questions in today's order and labels, the report's
 * curated population columns, and the assigned asset. Only usable dimensions
 * (today's quality rule) are returned.
 */
export function buildFigureDimensions(input: BuildFigureDimensionsInput): Array<FigureDimension> {
  const { meta, questionName, surveyQuestions, canonicalQuestionMap, assetContextItems, labels } = input
  const dimensions: Array<FigureDimension> = []

  if (meta.allSavedSegments && meta.allSavedSegments.values.length >= 2) {
    dimensions.push({
      key: ALL_SAVED_SEGMENTS_KEY,
      label: labels.allSubsegments,
      group: 'segment',
      cut: meta.allSavedSegments.cut,
      values: dimensionValues(meta.allSavedSegments),
      preferredValueId: null,
    })
  }

  meta.savedSegments.forEach((segment) => {
    dimensions.push({
      key: `segment:${segment.segmentId}`,
      label: segment.label,
      group: 'segment',
      cut: segment.cut,
      values: dimensionValues(segment),
      preferredValueId: segment.values.find((value) => !value.complement)?.id ?? null,
    })
  })

  const listedQuestions = new Map(meta.questions.map((entry) => [entry.questionId, entry]))
  const ownQuestionId = questionIdOf({ name: questionName }, canonicalQuestionMap)
  surveyQuestions
    .filter((question) => question?.name && question.name !== questionName && !question.isSegmentMapping)
    .forEach((question) => {
      const questionId = questionIdOf(question, canonicalQuestionMap)
      const entry = questionId && questionId !== ownQuestionId ? listedQuestions.get(questionId) : undefined
      if (!entry) {
        return
      }
      dimensions.push({
        key: `question:${question.name}`,
        label: String(question.title || question.name),
        group: 'survey',
        cut: entry.cut,
        values: dimensionValues(entry, (value) =>
          value.complement
            ? value.label
            : declaredValueText(question, value.id, assetContextItems) ?? value.label,
        ),
        preferredValueId: null,
      })
    })

  meta.population.forEach((entry) => {
    dimensions.push({
      key: `population:${entry.column}`,
      label: entry.label,
      group: 'population',
      cut: entry.cut,
      values: dimensionValues(entry),
      preferredValueId: null,
    })
  })

  if (meta.assignedAsset) {
    dimensions.push({
      key: 'asset',
      label: labels.assignedAsset,
      group: 'asset',
      cut: meta.assignedAsset.cut,
      values: dimensionValues(meta.assignedAsset),
      preferredValueId: null,
    })
  }

  return dimensions.filter(isUsableFigureDimension)
}

/**
 * The value a lane-merged card opens on: today's rule, a saved segment's
 * members first, else the largest group by the service's count.
 */
export function defaultFigureDimensionValue(dimension: FigureDimension): FigureDimensionValue | null {
  const present = dimension.values.filter((value) => value.n > 0)
  const preferred = present.find((value) => value.id === dimension.preferredValueId)
  if (preferred) {
    return preferred
  }
  return present.reduce<FigureDimensionValue | null>(
    (best, value) => (best == null || value.n > best.n ? value : best),
    null,
  )
}
